import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingCart, Minus, Plus, ArrowLeft, MessageCircle, Mail, Truck, ShieldCheck, Package, Store } from "lucide-react";
import { api, money } from "@/lib/api";
import { buildProductJsonLd, productImageAlt } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import { GorillaGuarantee } from "@/components/GorillaGuarantee";
import { ProductCard } from "@/components/ProductCard";
import { ProductBadges } from "@/components/ProductBadges";
import { ProductReviews } from "@/components/ProductReviews";
import { BRAND } from "@/data/brand";
import { toast } from "sonner";

function cleanHtml(raw) {
  if (!raw) return "";
  let s = String(raw)
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|h[1-6]|li)\s*>/gi, "\n")
    .replace(/<[^>]+>/g, "")            // strip remaining tags
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return s.replace(/\n{3,}/g, "\n\n").replace(/[ \t]{2,}/g, " ").trim();
}


export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [variantVid, setVariantVid] = useState("");
  const [reviewSummary, setReviewSummary] = useState(null);

  useEffect(() => {
    setNotFound(false);
    setSelectedImg(0);
    setVariantVid("");
    setReviewSummary(null);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        api
          .get("/products", { params: { category: r.data.category } })
          .then((rr) => setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 3)));
        api
          .get(`/reviews/summary`, { params: { product_id: r.data.id } })
          .then((rs) => setReviewSummary(rs.data))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="jm-container py-24 text-center">
        <h1 className="font-display text-4xl text-navy">Item not found</h1>
        <Link to="/shop" className="text-brick font-bold mt-4 inline-block">Back to shop</Link>
      </div>
    );

  if (!product) return <div className="jm-container py-24 text-center text-navy/50">Loading...</div>;

  const soldOut = product.sold_out === true;
  const isDropship = product.is_dropship === true;
  const tracked = product.track_inventory === true;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const hasOptions = isDropship && variants.length > 1;
  const needsReview = product.needs_variant_review === true;
  const selectedVariant = hasOptions ? variants.find((v) => v.vid === variantVid) : null;
  const optionChosen = !hasOptions || !!selectedVariant;
  const purchasable = !product.showcase && product.price != null && !soldOut && !needsReview;
  // Personal/resale items are limited to the exact units on hand. Dropship items
  // come from the supplier, so allow a modest cap (variant-level stock TBD).
  const maxQty = isDropship ? 10 : (tracked ? Math.max(1, product.stock || 0) : 99);
  const showQtySelector = maxQty > 1;
  const displayPrice = selectedVariant?.price != null ? selectedVariant.price : product.price;
  const img = product.images?.[selectedImg] || product.images?.[0];
  const canonical = `https://johnnymart.net/product/${product.slug}`;
  const plainDesc = cleanHtml(product.description).slice(0, 300);
  const jsonLd = buildProductJsonLd(product, reviewSummary, plainDesc);
  const imgAlt = productImageAlt(product);

  const handleAdd = () => {
    if (hasOptions && !selectedVariant) {
      toast.error("Please choose an option before adding to cart");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      price: displayPrice,
      image: selectedVariant?.image || product.images?.[0],
      max_qty: maxQty,
      is_dropship: isDropship,
      variant_vid: selectedVariant?.vid || null,
      variant_label: selectedVariant?.name || null,
    }, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page">
      <Helmet>
        <title>{`${product.name} | JohnnyMart`}</title>
        <meta name="description" content={plainDesc || `${product.name} at JohnnyMart — Ponte Vedra Beach resale shop.`} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | JohnnyMart`} />
        <meta property="og:description" content={plainDesc} />
        <meta property="og:url" content={canonical} />
        {product.images?.[0] && <meta property="og:image" content={product.images[0]} />}
        <meta name="twitter:card" content="summary_large_image" />
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>
      <div className="jm-container py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-navy/70 hover:text-brick font-bold text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <div>
            <div className="rounded-lg overflow-hidden border border-navy/15 bg-white aspect-square">
              {img ? (
                <img src={img} alt={imgAlt} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-navy/20 font-display text-5xl">JM</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap" data-testid="product-thumbnails">
                {product.images.map((u, i) => (
                  <button key={u} onClick={() => setSelectedImg(i)}
                    className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${i === selectedImg ? "border-brick" : "border-navy/15 hover:border-navy/40"}`}>
                    <img src={u} alt={`${imgAlt} - photo ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {product.video_url && (
              <video data-testid="product-video" src={product.video_url} controls playsInline
                className="mt-4 w-full rounded-lg border border-navy/15 bg-black max-h-[420px]" />
            )}
          </div>

          <div>
            <span className="inline-block bg-cream border border-navy/15 text-navy text-xs font-bold rounded-full px-3 py-1">
              {product.category}
            </span>
            {product.condition && (
              <span data-testid="product-condition" className="inline-block ml-2 bg-navy/5 border border-navy/15 text-navy/80 text-xs font-bold rounded-full px-3 py-1">
                {product.condition}
              </span>
            )}
            {product.badges?.length > 0 && (
              <div className="mt-3">
                <ProductBadges badges={product.badges} slug={product.slug} size="lg" />
              </div>
            )}
            <h1 data-testid="product-title" className="font-display text-4xl sm:text-5xl text-navy mt-4 leading-none">
              {product.name}
            </h1>
            <p data-testid="product-price" className="font-display text-3xl text-brick mt-4">
              {soldOut ? "Sold Out" : needsReview ? product.price_label || "Currently unavailable" : purchasable ? money(displayPrice) : product.price_label}
            </p>
            <p className="mt-5 text-ink/80 leading-relaxed whitespace-pre-line">{cleanHtml(product.description)}</p>

            {product.is_dropship && (
              <div
                data-testid="supplier-notice"
                className="mt-5 flex items-start gap-2.5 rounded-md border border-navy/15 bg-cream px-4 py-3 text-sm text-navy"
              >
                <Truck className="w-5 h-5 shrink-0 mt-0.5 text-brick" />
                <span><span className="font-bold">Shipped directly from supplier.</span> This item ships from our trusted partner warehouse and may take a little longer to arrive.</span>
              </div>
            )}

            {product.condition_details && (
              <div data-testid="condition-details" className="mt-4 rounded-md border border-navy/15 bg-white px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-navy/60">Condition details</p>
                <p className="mt-1 text-sm text-ink/80 leading-relaxed whitespace-pre-line">{product.condition_details}</p>
              </div>
            )}

            {hasOptions && (
              <div data-testid="variant-selector" className="mt-5">
                <label className="block text-sm font-bold uppercase tracking-wide text-navy/70">
                  Choose an option <span className="text-brick">*</span>
                </label>
                <select
                  data-testid="variant-select"
                  value={variantVid}
                  onChange={(e) => setVariantVid(e.target.value)}
                  className={`mt-2 w-full rounded-md border px-3 py-3 outline-none focus:border-brick bg-white text-navy ${variantVid ? "border-navy/25" : "border-brick"}`}
                >
                  <option value="">— Select size / color / option —</option>
                  {variants.map((v) => (
                    <option key={v.vid} value={v.vid}>
                      {v.name || v.sku || v.vid}{v.price != null ? ` — ${money(v.price)}` : ""}
                    </option>
                  ))}
                </select>
                {!variantVid && (
                  <p data-testid="variant-required-note" className="mt-1.5 text-sm font-semibold text-brick">
                    Please select an option before adding to cart.
                  </p>
                )}
              </div>
            )}

            {needsReview && (
              <div data-testid="needs-variant-review" className="mt-5 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
                <span><span className="font-bold">This item is being updated.</span> Its available options are being verified, so it can't be purchased right now. Please check back soon or <Link to={`/contact?product=${encodeURIComponent(product.name)}`} className="font-semibold underline">contact us</Link>.</span>
              </div>
            )}

            <ul className="mt-4 space-y-1.5 text-sm text-ink/70">
              <li className="flex items-center gap-2"><Package className="w-4 h-4 text-brick" /> Shipping available (USPS, calculated at checkout)</li>
              <li className="flex items-center gap-2"><Store className="w-4 h-4 text-brick" /> Local pickup available in Ponte Vedra Beach, FL</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brick" /> 7-day returns for items not as described — <Link to="/policies/returns" className="text-brick font-semibold ml-1">see policy</Link></li>
            </ul>

            {soldOut ? (
              <div className="mt-7">
                <button data-testid="sold-out-btn" disabled className="w-full inline-flex items-center justify-center gap-2 bg-navy/30 text-white font-bold uppercase rounded-md px-6 py-3.5 cursor-not-allowed">
                  Sold Out
                </button>
                <Link to={`/contact?product=${encodeURIComponent(product.name)}`} className="mt-3 flex items-center gap-2 text-navy/70 hover:text-brick text-sm font-semibold">
                  <Mail className="w-4 h-4" /> Ask about restock or similar items
                </Link>
              </div>
            ) : purchasable ? (
              <>
                <div className="mt-7 flex items-center gap-4">
                  {showQtySelector ? (
                    <div className="flex items-center border border-navy/25 rounded-md overflow-hidden">
                      <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5 text-navy hover:bg-cream">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span data-testid="qty-value" className="px-5 font-bold text-navy">{qty}</span>
                      <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} disabled={qty >= maxQty}
                        className="px-3 py-2.5 text-navy hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <span data-testid="qty-single" className="text-sm font-semibold text-navy/70 border border-navy/20 rounded-md px-4 py-2.5">Qty: 1 {!isDropship && "(only one available)"}</span>
                  )}
                  <button
                    data-testid="add-to-cart-btn"
                    onClick={handleAdd}
                    disabled={!optionChosen}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brick"
                  >
                    <ShoppingCart className="w-5 h-5" /> {optionChosen ? "Add to Cart" : "Select an option"}
                  </button>
                </div>
                {!isDropship && tracked && (
                  <p data-testid="stock-note" className={`mt-2 text-sm font-semibold ${product.stock <= 0 ? "text-red-600" : product.stock <= 3 ? "text-brick" : "text-green-700"}`}>
                    {product.stock <= 0
                      ? "Out of stock"
                      : product.stock === 1
                        ? "Only 1 in stock — one-of-a-kind find!"
                        : `${product.stock} in stock`}
                  </p>
                )}
              </>
            ) : (
              <Link
                to={`/contact?product=${encodeURIComponent(product.name)}`}
                data-testid="inquire-btn"
                className="mt-7 inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors"
              >
                <MessageCircle className="w-5 h-5" /> Ask About This Item
              </Link>
            )}

            <Link
              to={`/contact?product=${encodeURIComponent(product.name)}`}
              className="mt-3 flex items-center gap-2 text-navy/70 hover:text-brick text-sm font-semibold"
            >
              <Mail className="w-4 h-4" /> Have a question? Message us
            </Link>

            <div className="mt-8">
              <GorillaGuarantee compact />
            </div>
          </div>
        </div>

        <ProductReviews productId={product.id} />

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display text-3xl text-navy">More in {product.category}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
